import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { hasTargets } from "@/lib/verticals";

export function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[hsl(var(--primary))] border-t-transparent" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

export function AdminRoute({ children }) {
  const { user, loading, isFull } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!isFull) return <Navigate to="/dashboard" replace />;
  return children;
}

// PART 8 — Content & CMS is ADMIN-ONLY (leaders/managers cannot edit copy/config).
export function SuperAdminRoute({ children }) {
  const { user, loading, isCmsAdmin } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!isCmsAdmin) return <Navigate to="/dashboard" replace />;
  return children;
}

// Config pages (budgets, targets, sales, audit) — blocked for executives
export function ConfigRoute({ children }) {
  const { user, loading, canEditConfig } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!canEditConfig) return <Navigate to="/spends" replace />;
  return children;
}

// Targets — a config page, but a vertical lead whose assigned verticals have no
// applicable targets should not reach it even via a direct URL (nav is hidden too).
export function TargetsRoute({ children }) {
  const { user, loading, canEditConfig, isManager, assignedVerticals } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!canEditConfig) return <Navigate to="/spends" replace />;
  if (isManager && !hasTargets(assignedVerticals)) return <Navigate to="/dashboard" replace />;
  return children;
}

// Analysis pages — executives are limited to Spends Log + Invoices only
export function NonExecRoute({ children }) {
  const { user, loading, isExecutive } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (isExecutive) return <Navigate to="/spends" replace />;
  return children;
}

// Full-access only (admin/leader). Vertical leads (managers) are sent to their
// dashboard; executives to their spends ledger.
export function FullAccessRoute({ children }) {
  const { user, loading, isFull, isManager } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!isFull) return <Navigate to={isManager ? "/dashboard" : "/spends"} replace />;
  return children;
}

// Everyone except vertical leads (managers). Executives + full access allowed.
export function NotManagerRoute({ children }) {
  const { user, loading, isManager } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (isManager) return <Navigate to="/dashboard" replace />;
  return children;
}
